import { useState, useCallback, useMemo, useEffect } from 'react';
import { ParteProcesso, ParteRepresentada } from '../../interfaces';
import { FormularioParte, FormularioRepresentada, ModalExcluirState, MapaProcesso } from '../types';

let contadorIdTemporario = -1;

function gerarIdTemporario(): number {
    contadorIdTemporario--;
    return contadorIdTemporario;
}

interface UsePartesProps {
    partesProcesso: ParteProcesso[];
    partesRepresentadas: ParteRepresentada[];
    isEditing: boolean;
    onPartesChange?: (partes: ParteProcesso[]) => void;
    onPartesRepresentadasChange?: (partes: ParteRepresentada[]) => void;
    onChange?: (campo: string, valor: unknown) => void;
}

export function usePartes({
    partesProcesso,
    partesRepresentadas,
    isEditing,
    onPartesChange,
    onPartesRepresentadasChange,
    onChange
}: UsePartesProps) {
    const [partesEditando, setPartesEditando] = useState<ParteProcesso[]>([]);
    const [partesRepresentadasEditando, setPartesRepresentadasEditando] = useState<ParteRepresentada[]>([]);
    const [formularioParte, setFormularioParte] = useState<FormularioParte>({ tipo: '', nome: '' });
    const [formularioRepresentada, setFormularioRepresentada] = useState<FormularioRepresentada>({ nome: '' });
    const [modalExcluir, setModalExcluir] = useState<ModalExcluirState>({
        aberto: false,
        tipo: 'processo',
        id: 0,
        nome: ''
    });

    useEffect(() => {
        if (isEditing) {
            const partesProcessoArray = Array.isArray(partesProcesso) ? partesProcesso : [];
            const partesRepresentadasArray = Array.isArray(partesRepresentadas) ? partesRepresentadas : [];
            setPartesEditando([...partesProcessoArray]);
            setPartesRepresentadasEditando([...partesRepresentadasArray]);
        } else {
            setPartesEditando([]);
            setPartesRepresentadasEditando([]);
            setFormularioParte({ tipo: '', nome: '' });
            setFormularioRepresentada({ nome: '' });
        }
    }, [isEditing]);

    const mapaProcesso = useMemo<MapaProcesso>(() => {
        const controle: MapaProcesso = {};
        // Usa partesEditando quando em modo de edição, senão usa partesProcesso original
        const partesParaMapear = isEditing ? partesEditando : partesProcesso;
        for (const parte of partesParaMapear) {
            if (parte.id_parte_representada !== null) {
                controle[parte.id_parte_representada] = parte.tipo;
            }
        }
        return controle;
    }, [isEditing, partesEditando, partesProcesso]);

    const podeAdicionarParte = useMemo(
        () => formularioParte.tipo.trim() !== '' && formularioParte.nome.trim() !== '',
        [formularioParte.tipo, formularioParte.nome]
    );

    const podeAdicionarRepresentada = useMemo(
        () => formularioRepresentada.nome.trim().length > 0,
        [formularioRepresentada.nome]
    );

    const atualizarPartes = useCallback((novasPartes: ParteProcesso[], novasRepresentadas: ParteRepresentada[]) => {
        setPartesEditando(novasPartes);
        setPartesRepresentadasEditando(novasRepresentadas);
        
        if (onPartesChange) {
            onPartesChange(novasPartes);
        }
        if (onPartesRepresentadasChange) {
            onPartesRepresentadasChange(novasRepresentadas);
        }
        if (onChange) {
            onChange('partes_processo', novasPartes);
            onChange('partes_representadas', novasRepresentadas);
        }
    }, [onPartesChange, onPartesRepresentadasChange, onChange]);

    const handleAdicionarParte = useCallback(() => {
        if (!podeAdicionarParte) {
            return;
        }

        const idTemporario = gerarIdTemporario();
        const novaParte: ParteProcesso = {
            id_parte_processo: idTemporario,
            id_proposta: 0,
            nome: formularioParte.nome.trim(),
            tipo: formularioParte.tipo,
            id_parte_representada: null,
            criado: new Date().toISOString(),
            modificado: new Date().toISOString()
        };

        const novasPartes = [...partesEditando, novaParte];
        setFormularioParte({ tipo: '', nome: '' });
        atualizarPartes(novasPartes, partesRepresentadasEditando);
    }, [podeAdicionarParte, formularioParte, partesEditando, partesRepresentadasEditando, atualizarPartes]);

    const handleAdicionarRepresentada = useCallback(() => {
        if (!podeAdicionarRepresentada) {
            return;
        }

        const novaRepresentada: ParteRepresentada = {
            id_parte_representada: gerarIdTemporario(),
            id_proposta: 0,
            nome: formularioRepresentada.nome.trim(),
            id_parte_processo: null,
            criado: new Date().toISOString(),
            modificado: new Date().toISOString()
        };

        const novasRepresentadas = [...partesRepresentadasEditando, novaRepresentada];
        setFormularioRepresentada({ nome: '' });
        atualizarPartes(partesEditando, novasRepresentadas);
    }, [podeAdicionarRepresentada, formularioRepresentada, partesEditando, partesRepresentadasEditando, atualizarPartes]);

    const handleExcluirParte = useCallback((tipo: 'processo' | 'representada', id: number, nome: string) => {
        setModalExcluir({ aberto: true, tipo, id, nome });
    }, []);

    const confirmarExclusao = useCallback(() => {
        const { tipo, id } = modalExcluir;
        
        if (tipo === 'processo') {
            const parteIndex = partesEditando.findIndex(p => p.id_parte_processo === id);
            if (parteIndex === -1) {
                setModalExcluir({ aberto: false, tipo: 'processo', id: 0, nome: '' });
                return;
            }

            const parte = partesEditando[parteIndex];
            const novasPartes = partesEditando.filter(p => p.id_parte_processo !== id);
            let novasRepresentadas = [...partesRepresentadasEditando];

            if (parte.id_parte_representada !== null) {
                novasRepresentadas = novasRepresentadas.filter(
                    r => r.id_parte_representada !== parte.id_parte_representada
                );
            }

            atualizarPartes(novasPartes, novasRepresentadas);
        } else {
            const representadaIndex = partesRepresentadasEditando.findIndex(r => r.id_parte_representada === id);
            if (representadaIndex === -1) {
                setModalExcluir({ aberto: false, tipo: 'processo', id: 0, nome: '' });
                return;
            }

            const representada = partesRepresentadasEditando[representadaIndex];
            const novasRepresentadas = partesRepresentadasEditando.filter(r => r.id_parte_representada !== id);
            
            if (representada.id_parte_processo) {
                const parteIndex = partesEditando.findIndex(p => p.id_parte_processo === representada.id_parte_processo);
                if (parteIndex !== -1) {
                    const parteOriginal = partesEditando[parteIndex];
                    if (parteOriginal.id_parte_representada === id) {
                        const novasPartes = partesEditando.map((p, idx) => 
                            idx === parteIndex 
                                ? { ...p, id_parte_representada: null }
                                : p
                        );
                        atualizarPartes(novasPartes, novasRepresentadas);
                    } else {
                        atualizarPartes(partesEditando, novasRepresentadas);
                    }
                } else {
                    atualizarPartes(partesEditando, novasRepresentadas);
                }
            } else {
                atualizarPartes(partesEditando, novasRepresentadas);
            }
        }

        setModalExcluir({ aberto: false, tipo: 'processo', id: 0, nome: '' });
    }, [modalExcluir, partesEditando, partesRepresentadasEditando, atualizarPartes]);

    const alternarRepresentada = useCallback((parte: ParteProcesso) => {
        const index = partesEditando.findIndex(p => p.id_parte_processo === parte.id_parte_processo);
        if (index === -1) return;

        const estavaRepresentada = parte.id_parte_representada !== null;
        const novasPartes = partesEditando.map((p, idx) => 
            idx === index 
                ? { ...p, id_parte_representada: estavaRepresentada ? null : gerarIdTemporario() }
                : p
        );
        let novasRepresentadas = [...partesRepresentadasEditando];

        if (!estavaRepresentada) {
            const novaRepresentada: ParteRepresentada = {
                id_parte_representada: novasPartes[index].id_parte_representada as number,
                id_proposta: parte.id_proposta,
                nome: parte.nome,
                id_parte_processo: parte.id_parte_processo,
                criado: new Date().toISOString(),
                modificado: new Date().toISOString()
            };
            novasRepresentadas.push(novaRepresentada);
        } else {
            novasRepresentadas = novasRepresentadas.filter(
                r => r.id_parte_representada !== parte.id_parte_representada
            );
        }

        atualizarPartes(novasPartes, novasRepresentadas);
    }, [partesEditando, partesRepresentadasEditando, atualizarPartes]);

    const fecharModal = useCallback(() => {
        setModalExcluir({ aberto: false, tipo: 'processo', id: 0, nome: '' });
    }, []);

    return {
        partesEditando,
        partesRepresentadasEditando,
        formularioParte,
        formularioRepresentada,
        modalExcluir,
        mapaProcesso,
        podeAdicionarParte,
        podeAdicionarRepresentada,
        setFormularioParte,
        setFormularioRepresentada,
        handleAdicionarParte,
        handleAdicionarRepresentada,
        handleExcluirParte,
        confirmarExclusao,
        alternarRepresentada,
        fecharModal
    };
}

